export const VIEWPORT_BURST_PROJECTED_PAIR_TARGETS = 3;
export const VIEWPORT_BURST_PROJECTED_SINGLETON_TARGETS = 8;
export const VIEWPORT_BURST_PROJECTED_MAX_TRANSIENT_SINGLETONS = 2;

const finiteSafeInteger = value => Number.isSafeInteger(Number(value)) ? Number(value) : null;

export const createViewportBurstProjectionSlices = ({
  acceptedTargetSlice,
  direction,
  stepMagnitude,
  settledStride,
  transientStride,
  maxSlice,
  entryLimit = 23,
} = {}) => {
  const accepted = finiteSafeInteger(acceptedTargetSlice);
  const maximum = finiteSafeInteger(maxSlice);
  const step = finiteSafeInteger(stepMagnitude);
  const settled = finiteSafeInteger(settledStride);
  const transient = finiteSafeInteger(transientStride);
  const limit = finiteSafeInteger(entryLimit);
  const dir = Number(direction) < 0 ? -1 : (Number(direction) > 0 ? 1 : 0);
  if (accepted === null || maximum === null || step === null || settled === null
      || transient === null || limit === null || dir === 0
      || maximum < 0 || accepted < 0 || accepted > maximum
      || step <= 0 || settled <= 0 || transient < settled || limit <= 0) return [];

  const projected = [];
  const seen = new Set();
  const add = candidate => {
    if (projected.length >= limit || !Number.isSafeInteger(candidate)) return false;
    const bounded = Math.max(0, Math.min(maximum, candidate));
    if (bounded === accepted || seen.has(bounded)) return false;
    seen.add(bounded);
    projected.push(bounded);
    return true;
  };

  for (let pairTarget = 1; pairTarget <= VIEWPORT_BURST_PROJECTED_PAIR_TARGETS; pairTarget += 1) {
    add(accepted + (dir * step * pairTarget));
  }

  const singletonBase = accepted + (dir * step * VIEWPORT_BURST_PROJECTED_PAIR_TARGETS);
  const transientExcess = transient - settled;
  for (let singletonOrdinal = 1;
    singletonOrdinal <= VIEWPORT_BURST_PROJECTED_SINGLETON_TARGETS;
    singletonOrdinal += 1) {
    const maxTransientCount = Math.min(
      VIEWPORT_BURST_PROJECTED_MAX_TRANSIENT_SINGLETONS,
      singletonOrdinal,
    );
    for (let transientCount = 0; transientCount <= maxTransientCount; transientCount += 1) {
      add(singletonBase + (dir * (
        (singletonOrdinal * settled) + (transientCount * transientExcess)
      )));
    }
  }
  return projected;
};

export const createViewportBurstProjectionFrontierSlices = ({
  currentTargetSlice,
  direction,
  stepMagnitude,
  settledStride,
  transientStride,
  maxSlice,
  entryLimit = 7,
} = {}) => {
  const current = finiteSafeInteger(currentTargetSlice);
  const maximum = finiteSafeInteger(maxSlice);
  const step = finiteSafeInteger(stepMagnitude);
  const settled = finiteSafeInteger(settledStride);
  const transient = finiteSafeInteger(transientStride);
  const limit = finiteSafeInteger(entryLimit);
  const dir = Number(direction) < 0 ? -1 : (Number(direction) > 0 ? 1 : 0);
  if (current === null || maximum === null || step === null || settled === null
      || transient === null || limit === null || dir === 0
      || maximum < 0 || current < 0 || current > maximum
      || step <= 0 || settled <= 0 || transient < settled || limit <= 0) return [];

  const projected = [];
  const seen = new Set();
  const addDistance = distance => {
    if (projected.length >= limit || !Number.isSafeInteger(distance) || distance <= 0) return false;
    const bounded = Math.max(0, Math.min(maximum, current + (dir * distance)));
    if (bounded === current || seen.has(bounded)) return false;
    seen.add(bounded);
    projected.push(bounded);
    return true;
  };

  // Put the two likely one-input outcomes first, while keeping a repeated
  // paired-burst endpoint in the same bounded decode wave. Cover all settled/
  // transient outcomes through the next three singleton bursts: the browser
  // may receive a later trusted input before the preceding accepted-paint
  // observer settles, so that third target must already remain warm.
  [
    settled,
    transient,
    step,
    settled * 2,
    settled + transient,
    transient * 2,
    settled * 3,
    (settled * 2) + transient,
  ].forEach(addDistance);
  return projected;
};

export const createViewportBurstProjectionPlan = ({
  currentTargetSlice,
  direction,
  stepMagnitude,
  settledStride,
  transientStride,
  maxSlice,
  continuingSession = false,
  consumedInputCount = 0,
} = {}) => {
  const consumed = finiteSafeInteger(consumedInputCount);
  if (consumed === null || consumed < 1) return { mode: 'invalid', slices: [] };
  if (continuingSession && consumed > 1) return { mode: 'preserve', slices: [] };
  if (continuingSession) {
    return {
      mode: 'frontier',
      slices: createViewportBurstProjectionFrontierSlices({
        currentTargetSlice,
        direction,
        stepMagnitude,
        settledStride,
        transientStride,
        maxSlice,
        entryLimit: 7,
      }),
    };
  }
  return {
    mode: 'lattice',
    slices: createViewportBurstProjectionSlices({
      acceptedTargetSlice: currentTargetSlice,
      direction,
      stepMagnitude,
      settledStride,
      transientStride,
      maxSlice,
      entryLimit: 23,
    }),
  };
};
